import React from "react";
import { ExternalLink, ArrowRight } from "lucide-react";
import BrowserFrame from "../ui/BrowserFrame";
import { stripScheme } from "../../../lib/projectHelpers";

const FRAME_TILT_STYLE = {
  transform: "perspective(1400px) rotateY(-1.5deg) rotateX(0.6deg)",
};

const FeaturedFrame = ({ project }) => {
  if (project.thumb) {
    return <BrowserFrame url={project.url} src={project.thumb} alt={project.name} />;
  }
  return (
    <BrowserFrame url="yourbrand.com" showBar>
      <div className="aspect-[16/10] flex flex-col items-center justify-center text-center px-8 bg-black">
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-4">
          Open Slot
        </div>
        <div className="font-serif text-3xl md:text-4xl text-themed max-w-lg leading-tight">
          Your brand could live here.
        </div>
        <div className="mt-6 text-sm text-themed-muted max-w-md">
          Built from your Momentum Personal Action Plan.
        </div>
      </div>
    </BrowserFrame>
  );
};

const FeaturedSlide = ({
  project,
  index,
  total,
  onCaseStudy,
  onScrollToBlueprint,
  carouselControls,
}) => (
  <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
    <div className="lg:col-span-7 relative">
      <div className="transition-transform duration-700 ease-out" style={FRAME_TILT_STYLE}>
        <FeaturedFrame project={project} />
      </div>
    </div>

    <div className="lg:col-span-5">
      <div className="text-[10px] tracking-[0.4em] uppercase text-gold/80 mb-4">
        {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
      </div>
      <h3
        data-testid="showcase-project-name"
        className="font-serif text-4xl md:text-5xl text-themed leading-tight"
      >
        {project.name}
      </h3>
      {project.url && (
        <a
          href={project.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="showcase-featured-domain"
          className="inline-flex items-center gap-2 mt-3 text-[11px] tracking-[0.3em] uppercase text-gold/80 hover:text-gold"
        >
          {stripScheme(project.url)} <ExternalLink size={12} />
        </a>
      )}

      <p className="mt-6 text-themed-muted leading-relaxed">{project.brief}</p>

      <div className="mt-6 flex flex-wrap gap-2">
        {project.tags.map((t) => (
          <span
            key={t}
            className="text-[10px] tracking-[0.2em] uppercase text-gold/85 border border-gold/30 px-3 py-1 rounded-full"
          >
            {t}
          </span>
        ))}
      </div>

      <div className="mt-10 flex flex-col sm:flex-row gap-3">
        {project.url ? (
          <a
            href={project.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="showcase-visit-button"
            className="btn-gold inline-flex items-center gap-2 justify-center"
          >
            Visit Site <ExternalLink size={13} />
          </a>
        ) : (
          <button
            onClick={onScrollToBlueprint}
            data-testid="showcase-blueprint-button"
            className="btn-gold inline-flex items-center gap-2 justify-center"
          >
            Start Your Personal Action Plan
          </button>
        )}
        <button
          data-testid="showcase-case-study-button"
          onClick={() => onCaseStudy(project)}
          className="btn-ghost-gold inline-flex items-center gap-2 justify-center"
        >
          Read Case Study <ArrowRight size={13} />
        </button>
      </div>

      {carouselControls}
    </div>
  </div>
);

export default FeaturedSlide;
